import { IErrorApiResponse, IOrder } from '@wyz/types';
import { DateUtils } from '@wyz/utils';
import { isNil } from 'lodash-es';
import {
  IAcceptOrderItemPayload,
  IAcceptOrderValidationErrorResponse,
  ISuggestedOffer,
  PurchaseErrorCodeKeys,
} from '~/purchase/types';

export function applyOfferChanges(
  error: IErrorApiResponse<
    IAcceptOrderValidationErrorResponse,
    PurchaseErrorCodeKeys
  >,
  previousItems: Array<IAcceptOrderItemPayload>,
) {
  const errorsBySupplierKey = error.errors.reduce(
    (acc, error) => ({
      ...acc,
      [error.supplierId]: error,
    }),
    {} as Record<string, (typeof error.errors)[number]>,
  );
  const newItems = previousItems.map((item) => {
    const error = errorsBySupplierKey[item.supplierId];
    const patch: Partial<typeof item> = {};
    if (error?.deliveryDateChanged) {
      patch.deliveryDate = error.actual.deliveryDate;
    }
    if (error?.priceChanged) {
      patch.sellingPrice = error.actual.sellingPrice;
    }
    if (error?.quantityChanged) {
      patch.quantity = error.actual.availableQuantity;
    }
    return {
      ...item,
      ...patch,
    };
  });
  return newItems;
}

export const getMappedChanges = (
  suggestedOffers: ISuggestedOffer[],
  changes: IAcceptOrderValidationErrorResponse[],
  order: IOrder,
) => {
  const mappedChanges = new Map<string, IAcceptOrderValidationErrorResponse>();
  // in that case, we check diff between suggested offers and current order
  if (changes.length === 0 && order.tyreLines.length > 0) {
    const tyre = order.tyreLines.at(0);
    const currentOffer = suggestedOffers.find((suggestedOffer) => {
      if (suggestedOffer.offer.supplier.id !== order.supplierId) {
        return false;
      }
      const orderWarehouseId = tyre?.warehouseId;
      const supplierWarehouseId = suggestedOffer.offer.supplier
        .extendedManufConfiguration?.enabled
        ? suggestedOffer.offer.supplier.extendedManufConfiguration.warehouseId
        : undefined;
      const isExtendedManufOrder =
        !isNil(orderWarehouseId) &&
        !isNil(supplierWarehouseId) &&
        orderWarehouseId === supplierWarehouseId;

      return isExtendedManufOrder === suggestedOffer.offer.extendedManufacturer;
    });
    if (currentOffer) {
      const priceR =
        currentOffer.offer.redistributionSellingPrice === tyre?.priceCustomer
          ? undefined
          : currentOffer.offer.redistributionSellingPrice;
      const estimatedDate =
        currentOffer.offer.deliveryDate &&
        DateUtils.formatTimestamp(new Date(currentOffer.offer.deliveryDate), {
          format: DateUtils.DATE_FORMAT.DMY,
        }) ===
          DateUtils.formatTimestamp(new Date(order.dateDelivered), {
            format: DateUtils.DATE_FORMAT.DMY,
          })
          ? undefined
          : currentOffer.offer.deliveryDate;
      const quantity =
        currentOffer.offer.availableQuantity >= (tyre?.quantity ?? 0)
          ? undefined
          : currentOffer.offer.availableQuantity;

      mappedChanges.set(currentOffer.offerId, {
        supplierId: currentOffer.offer.supplier.id,
        deliveryDate: estimatedDate,
        deliveryDateChanged: !!estimatedDate,
        priceChanged: !!priceR,
        quantityChanged: !!quantity,
        actual: {
          sellingPrice: priceR,
          availableQuantity: quantity,
          deliveryDate: estimatedDate,
        },
      });
      return mappedChanges;
    }
  }
  suggestedOffers.forEach((suggestedOffer) => {
    if (!mappedChanges.has(suggestedOffer.offerId)) {
      const diff = changes.find(
        (change) => change.supplierId === suggestedOffer.offer.supplier.id,
      );

      if (
        diff?.priceChanged ||
        diff?.quantityChanged ||
        diff?.deliveryDateChanged
      ) {
        mappedChanges.set(suggestedOffer.offerId, diff);
      }
    }
  });
  return mappedChanges;
};
